import { collection, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getCurrentMillis } from '../utils/dateUtils';
import type { Event } from '../types';

const EVENTS_REF = 'Events';

const isFirebaseAvailable = () => {
  if (!db) {
    console.error('Firebase not available');
    return false;
  }
  return true;
};

export const getAllEvents = async (): Promise<Event[]> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const eventsRef = collection(db, EVENTS_REF);
    const querySnapshot = await getDocs(eventsRef);
    const events: Event[] = [];
    querySnapshot.forEach((doc) => {
      const eventData = doc.data() as Event;
      events.push({ ...eventData, id: doc.id });
    });
    return events;
  } catch (error) {
    console.error('Error fetching events:', error);
    throw error;
  }
};

export const getEventById = async (eventId: string): Promise<Event | null> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const eventRef = doc(db, EVENTS_REF, eventId);
    const eventSnap = await getDoc(eventRef);
    if (eventSnap.exists()) {
      return { ...eventSnap.data() as Event, id: eventSnap.id };
    }
    return null;
  } catch (error) {
    console.error('Error fetching event:', error);
    throw error;
  }
};

export const getEventsListByFileId = async (fileId: string): Promise<Event[]> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const eventsRef = collection(db, EVENTS_REF);
    const q = query(eventsRef, where('legalFileID', '==', fileId));
    const querySnapshot = await getDocs(q);
    const events: Event[] = [];
    querySnapshot.forEach((doc) => {
      const eventData = doc.data() as Event;
      events.push({ ...eventData, id: doc.id });
    });
    return events;
  } catch (error) {
    console.error('Error fetching events by file ID:', error);
    throw error;
  }
};

export const getEventsByClient = async (clientId: string): Promise<Event[]> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const eventsRef = collection(db, EVENTS_REF);
    const q = query(eventsRef, where('clientID', '==', clientId));
    const querySnapshot = await getDocs(q);
    const events: Event[] = [];
    querySnapshot.forEach((doc) => {
      const eventData = doc.data() as Event;
      events.push({ ...eventData, id: doc.id });
    });
    return events;
  } catch (error) {
    console.error('Error fetching events by client:', error);
    throw error;
  }
};

export const getEventsByOffice = async (officeId: string): Promise<Event[]> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const eventsRef = collection(db, EVENTS_REF);
    const q = query(eventsRef, where('officeID', '==', officeId));
    const querySnapshot = await getDocs(q);
    const events: Event[] = [];
    querySnapshot.forEach((doc) => {
      const eventData = doc.data() as Event;
      events.push({ ...eventData, id: doc.id });
    });
    return events;
  } catch (error) {
    console.error('Error fetching events by office:', error);
    throw error;
  }
};

export const getEventsByStatus = async (status: number): Promise<Event[]> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const eventsRef = collection(db, EVENTS_REF);
    const q = query(eventsRef, where('status', '==', status));
    const querySnapshot = await getDocs(q);
    const events: Event[] = [];
    querySnapshot.forEach((doc) => {
      const eventData = doc.data() as Event;
      events.push({ ...eventData, id: doc.id });
    });
    return events;
  } catch (error) {
    console.error('Error fetching events by status:', error);
    throw error;
  }
};

export const fetchEventsByLawyerID = async (lawyerId: string): Promise<Event[]> => {
  try {
    console.log('🔍 Debug - fetchEventsByLawyerID called with lawyerId:', lawyerId);
    
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    
    console.log('🔄 Debug - Fetching events for lawyer ID:', lawyerId);
    
    const eventsRef = collection(db, EVENTS_REF);
    
    // Try both lawyerId and lawyerID fields
    const q1 = query(eventsRef, where('lawyerId', '==', lawyerId));
    const q2 = query(eventsRef, where('lawyerID', '==', lawyerId));
    
    const [snapshot1, snapshot2] = await Promise.all([
      getDocs(q1),
      getDocs(q2)
    ]);
    
    console.log('📊 Debug - Events found with lawyerId:', snapshot1.size);
    console.log('📊 Debug - Events found with lawyerID:', snapshot2.size);
    
    const events: Event[] = [];
    const processedIds = new Set<string>();
    
    // Process results from lawyerId query
    snapshot1.forEach((doc) => {
      if (!processedIds.has(doc.id)) {
        const eventData = doc.data() as Event;
        const event = { ...eventData, id: doc.id };
        events.push(event);
        console.log('🔍 Debug - Processing event doc (lawyerId):', doc.id, {
          lawyerId: event.lawyerId,
          lawyerID: event.lawyerID,
          date: event.date,
          startDate: event.startDate,
          title: eventData.legalFileTitle || eventData.placeName,
          status: event.status,
          clientName: event.clientName
        });
        processedIds.add(doc.id);
      }
    });
    
    // Process results from lawyerID query
    snapshot2.forEach((doc) => {
      if (!processedIds.has(doc.id)) {
        const eventData = doc.data() as Event;
        const event = { ...eventData, id: doc.id };
        events.push(event);
        console.log('🔍 Debug - Processing event doc (lawyerID):', doc.id, {
          lawyerId: event.lawyerId,
          lawyerID: event.lawyerID,
          date: event.date,
          startDate: event.startDate,
          title: eventData.legalFileTitle || eventData.placeName,
          status: event.status,
          clientName: event.clientName
        });
        processedIds.add(doc.id);
      }
    });
    
    console.log('✅ Debug - fetchEventsByLawyerID completed, returning', events.length, 'events');
    return events;
  } catch (error) {
    console.error('❌ Error fetching events by lawyer ID:', error);
    throw error;
  }
};

export const addEvent = async (eventData: Omit<Event, 'id'>): Promise<string> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const eventsRef = collection(db, EVENTS_REF);
    const docRef = await addDoc(eventsRef, eventData);
    return docRef.id;
  } catch (error) {
    console.error('Error adding event:', error);
    throw error;
  }
};

export const updateEvent = async (eventId: string, eventData: Partial<Event>): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const eventRef = doc(db, EVENTS_REF, eventId);
    await updateDoc(eventRef, eventData);
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

export const deleteEvent = async (eventId: string): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const eventRef = doc(db, EVENTS_REF, eventId);
    await deleteDoc(eventRef);
  } catch (error) {
    console.error('Error deleting event:', error);
    throw error;
  }
};

export const archiveEvent = async (eventId: string): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const eventRef = doc(db, EVENTS_REF, eventId);
    await updateDoc(eventRef, { isArchived: true });
  } catch (error) {
    console.error('Error archiving event:', error);
    throw error;
  }
};

export const createEvent = async (eventData: Omit<Event, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>): Promise<string> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    
    const now = getCurrentMillis();
    const eventToCreate: Omit<Event, 'id'> = {
      ...eventData,
      createdAt: now,
      updatedAt: now,
      createdBy: eventData.lawyerID,
      updatedBy: eventData.lawyerID,
    };
    
    const eventsRef = collection(db, EVENTS_REF);
    const docRef = await addDoc(eventsRef, eventToCreate);
    
    console.log('✅ Event created successfully with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ Error creating event:', error);
    throw error;
  }
}; 